import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { InvoiceService } from "@services";
import utils from "@utils";
import { SendToContactModal, Loader } from "../../../components";

import "./BulkInvoicePreview.scss";

const BulkInvoicePreview = ({ router, user }) => {
	const { lang } = global;
	const {
		location: { state: routerState },
		push,
	} = router;
	const { TimeZoneId } = user;
	// router state sometimes becomes undefined after a number of renders, so we need to store the first instance locally
	const [state] = useState(routerState);
	const [loading, setLoading] = useState(true);
	const [invoice, setInvoice] = useState({});
	const [showSendModal, setShowSendModal] = useState(false);
	const [invoiceSent, setInvoiceSent] = useState(false);

	const goToPayments = () => {
		push("/payments/list/review");
	};

	/**
	 * Sends an invoice to the email of the specified contact
	 * @param {Number} contactId The uniqe id of the contact which the invoice is being sent to
	 */
	const sendInvoice = contactId => {
		setLoading(true);
		const queryParams = {};
		if (contactId) queryParams.contactId = contactId;
		InvoiceService.sendInvoiceToClient(queryParams, { invoiceIds: state.InvoiceIds })
			.then(() => {
				setShowSendModal(false);
				setInvoiceSent(true);
			})
			.catch(() =>
				utils.notify({
					type: "error",
					message: "Error sending invoice to client",
					timeOut: 1500,
				}),
			)
			.finally(() => {
				setLoading(false);
			});
	};

	const handleSendBulkInvoice = () => {
		const {
			Organization: { Id: agencyId },
		} = user;
		if (agencyId === state.agencyId) setShowSendModal(true);
		else sendInvoice();
	};
	useEffect(() => {
		if (invoiceSent) {
			goToPayments();
		}
	}, [invoiceSent]);

	useEffect(() => {
		if (state) {
			const { InvoiceIds } = state;
			InvoiceService.getInvoiceSummary({ data: { InvoiceIds } })
				.then(({ data }) => {
					let { TransactionSummaryItems } = data;
					TransactionSummaryItems = TransactionSummaryItems.map(item => {
						if (item.AdditionalInvoiceItems.length > 0) {
							return {
								...item,
								AdditionalItemTotal: item.AdditionalInvoiceItems.map(
									({ ItemSubtotal }) => parseFloat(ItemSubtotal),
								).reduce((t, c) => t + c),
							};
						}
						return { ...item, AdditionalItemTotal: 0 };
					});
					setInvoice({ ...data, TransactionSummaryItems });
					setLoading(false);
					push("/payments/invoicepreview");
				})
				.catch(() => goToPayments());
		} else {
			goToPayments();
		}
	}, []);

	return (
		<div
			id="bulk-invoice-preview"
			className="container"
		>
			<Loader active={loading} />
			<div className="row">
				<div className="col-xs-12">
					<h1>{lang.t("Bulk Invoice Preview")}</h1>
				</div>
			</div>
			<div className="row table-headers hidden-xs">
				<div className="col-sm-1">
					<strong>{lang.t("Invoice")}</strong>
				</div>
				<div className="col-sm-4">
					<strong>{lang.t("Job")}</strong>
				</div>
				<div className="col-sm-3">
					<strong>{lang.t("Officer")}</strong>
				</div>
				<div className="col-sm-1">
					<strong>{lang.t("Hours")}</strong>
				</div>
				<div className="col-sm-1 text-right">
					<strong>{lang.t("Rate/Hr")}</strong>
				</div>
				<div className="col-sm-2 text-right">
					<strong>{lang.t("Amount Due")}</strong>
				</div>
			</div>
			<div className="line-items">
				<For
					each="item"
					of={invoice.TransactionSummaryItems || []}
				>
					<div
						key={`line-item_${item.AssignmentId}`}
						className="row"
					>
						<div className="col-sm-1">
							<Link to={`/payments/${item.InvoiceId}`}>
								<span className="visible-xs-inline">{lang.t("Invoice")} #</span>
								{item.InvoiceId}
							</Link>
						</div>
						<div className="col-sm-4">
							{item.Location}
							<br />
							{utils.formatDate(
								new Date(`${item.StartDateTime}Z`),
								"en-US",
								TimeZoneId,
							)}
							<br />
							<small className="text-muted">
								{utils.formatTime(
									new Date(`${item.StartDateTime}Z`),
									true,
									TimeZoneId,
								)}
								&nbsp;-&nbsp;
								{utils.formatTime(
									new Date(`${item.EndDateTime}Z`),
									true,
									TimeZoneId,
								)}
							</small>
						</div>
						<div className="col-sm-3">
							<strong className="visible-xs-inline">
								{lang.t("Officer")}:&nbsp;
							</strong>
							{item.Officer}
						</div>
						<div className="col-xs-4 col-sm-1">
							{item.Hours}
							<span className="visible-xs-inline">
								{item.Hours === 1 ? " hr" : " hrs"}
							</span>
						</div>
						<div className="col-xs-4 col-sm-1 rate">
							{utils.currencyFormatter(item.Rate)}
							<span className="visible-xs-inline">/hr</span>
						</div>
						<div className="col-xs-4 col-sm-2 text-right">
							<strong className="visible-xs-inline">{lang.t("Total")}:&nbsp;</strong>
							{utils.currencyFormatter(item.Amount)}
						</div>
						<If condition={item.JurisdictionFees.length > 0}>
							<div className="col-sm-7 col-sm-offset-5">
								<strong>{lang.t("Jurisdiction Fees")}</strong>
							</div>
						</If>
						<For
							each="additem"
							of={item.JurisdictionFees}
						>
							<div
								className="col-xs-12 additem"
								key={`${item.InvoiceId}_${additem.ItemName.replace(" ", "")}`}
							>
								<div className="col-xs-3 col-sm-offset-5">{additem.ItemName}</div>
								<div className="col-xs-3 col-sm-1">
									{additem.ItemHours}
									<span className="visible-xs-inline">
										{item.Hours === 1 ? " hr" : " hrs"}
									</span>
								</div>
								<div className="col-xs-3 col-sm-1 rate">
									{utils.currencyFormatter(parseFloat(additem.ItemRate))}
									<span className="visible-xs-inline">/hr</span>
								</div>
								<div className="col-xs-3 col-sm-2 text-right">
									{utils.currencyFormatter(parseFloat(additem.ItemSubtotal))}
								</div>
							</div>
						</For>
						<If condition={item.JurisdictionVehicleFees.length > 0}>
							<div className="col-sm-7 col-sm-offset-5">
								<strong>{lang.t("Vehicle Fees")}</strong>
							</div>
						</If>
						<For
							each="additem"
							of={item.JurisdictionVehicleFees}
						>
							<div
								className="col-xs-12 additem"
								key={`${item.InvoiceId}_${additem.ItemName.replace(" ", "")}`}
							>
								<div className="col-xs-3 col-sm-offset-5">{additem.ItemName}</div>
								<div className="col-xs-3 col-sm-1">
									{additem.ItemHours}
									<span className="visible-xs-inline">
										{item.Hours === 1 ? " hr" : " hrs"}
									</span>
								</div>
								<div className="col-xs-3 col-sm-1 rate">
									{utils.currencyFormatter(parseFloat(additem.ItemRate))}
									<span className="visible-xs-inline">/hr</span>
								</div>
								<div className="col-xs-3 col-sm-2 text-right">
									{utils.currencyFormatter(parseFloat(additem.ItemSubtotal))}
								</div>
							</div>
						</For>
						<If condition={item.AdditionalInvoiceItems.length > 0}>
							<div className="col-sm-7 col-sm-offset-5">
								<strong>{lang.t("Additional Items")}</strong>
							</div>
						</If>
						<For
							each="additem"
							of={item.AdditionalInvoiceItems}
						>
							<div
								className="col-xs-12 additem"
								key={`${item.InvoiceId}_${additem.ItemName.replace(" ", "")}`}
							>
								<div className="col-xs-3 col-sm-offset-5">{additem.ItemName}</div>
								<div className="col-xs-3 col-sm-1">
									{additem.ItemHours}
									<span className="visible-xs-inline">
										{item.Hours === 1 ? " hr" : " hrs"}
									</span>
								</div>
								<div className="col-xs-3 col-sm-1 rate">
									{utils.currencyFormatter(parseFloat(additem.ItemRate))}
									<span className="visible-xs-inline">/hr</span>
								</div>
								<div className="col-xs-3 col-sm-2 text-right">
									{utils.currencyFormatter(parseFloat(additem.ItemSubtotal))}
								</div>
							</div>
						</For>
					</div>
				</For>
				<For
					each="coord"
					of={(invoice.Organizations || []).filter(({ Amount }) => Amount > 0)}
					index="idx"
				>
					<div
						key={(coord.Name || `coord_${idx}`).toLowerCase().replace(" ", "")}
						className="row"
					>
						<div className="col-xs-0 col-sm-5" />
						<div className="col-xs-12 col-sm-3">
							<strong>{lang.t("Coordination")}</strong>
							<br />
							{coord.Name}
						</div>
						<div className="col-xs-4 col-sm-1">
							{coord.Hours}
							<span className="visible-xs-inline text-lowercase">
								{" "}
								{lang.t("Hours")}
							</span>
						</div>
						<div className="col-xs-4 col-sm-1 rate">
							{utils.currencyFormatter(coord.Rate)}
							<span className="visible-xs-inline">/hr</span>
						</div>
						<div className="col-xs-4 col-sm-2 text-right">
							<strong className="visible-xs-inline">{lang.t("Total")}:&nbsp;</strong>
							{utils.currencyFormatter(coord.Amount)}
						</div>
					</div>
				</For>
			</div>
			<div className="receipt-totals">
				<div className="row">
					<div className="col-xs-8 col-sm-10 text-right">
						<strong>{lang.t("Total Officer Pay")}</strong>
					</div>
					<div className="col-xs-4 col-sm-2 text-right">
						{utils.currencyFormatter(invoice.TotalOfficerPay || 0)}
					</div>
				</div>
				<div className="row">
					<div className="col-xs-8 col-sm-10 text-right">
						<strong>{lang.t("Jurisdiction Fees")}</strong>
					</div>
					<div className="col-xs-4 col-sm-2 text-right">
						{utils.currencyFormatter(invoice.TotalJurisdictionFee || 0)}
					</div>
				</div>
				<If condition={invoice.TotalOrganizationFee > 0}>
					<div className="row">
						<div className="col-xs-8 col-sm-10 text-right">
							<strong>{lang.t("Coordination Fees")}</strong>
						</div>
						<div className="col-xs-4 col-sm-2 text-right">
							{utils.currencyFormatter(invoice.TotalOrganizationFee)}
						</div>
					</div>
				</If>
				<div className="row">
					<div className="col-xs-8 col-sm-10 text-right">
						<strong>{lang.t("Service Fees")}</strong>
					</div>
					<div className="col-xs-4 col-sm-2 text-right">
						{utils.currencyFormatter(invoice.RKPayFee || 0)}
					</div>
				</div>
				<div className="row">
					<div className="col-xs-8 col-sm-10 text-right">
						<strong>{lang.t("Tax")}</strong>
					</div>
					<div className="col-xs-4 col-sm-2 text-right">
						{utils.currencyFormatter(invoice.Tax || 0)}
					</div>
				</div>
				<div className="row">
					<div className="col-xs-8 col-sm-10 text-right">
						<strong>{lang.t("Total")}</strong>
					</div>
					<div className="col-xs-4 col-sm-2 text-right">
						<strong>{utils.currencyFormatter(invoice.Total || 0)}</strong>
					</div>
				</div>
			</div>
			<div className="row">
				<div className="col-xs-12 col-sm-offset-9 col-sm-3 col-lg-offset-10 col-lg-2">
					<button
						type="button"
						id="send-bulk-invoices"
						className="btn btn-block btn-primary"
						onClick={handleSendBulkInvoice}
					>
						{lang.t("Send as Single Invoice")}
					</button>
				</div>
			</div>
			<SendToContactModal
				show={showSendModal}
				onHide={() => setShowSendModal(false)}
				user={user}
				clientId={state?.clientId}
				onSubmit={contactId => sendInvoice(contactId)}
			/>
		</div>
	);
};

BulkInvoicePreview.propTypes = {
	router: PropTypes.object,
	user: PropTypes.object,
};

BulkInvoicePreview.defaultProps = {
	router: {},
	user: {},
};

export default BulkInvoicePreview;
